import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { Loader2, Rocket, Users } from 'lucide-react';

const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

/**
 * Pre-launch bookings switch.
 *
 * Practitioners are being onboarded before launch to get acclimated, and they can't build a
 * booking page behind a paywall (owner, Jun 2026). While this is on, the bookings gate lets
 * every practitioner through and no Stripe subscription record is created — the alternative
 * was practitioners "subscribing" with a sandbox card, which leaves test-mode Stripe
 * customers that are worthless the moment live keys go in.
 *
 * Turning it OFF is launch day: every practitioner is emailed and notified to set up direct
 * deposit and start their subscription.
 */
export default function PrelaunchBookingsCard() {
  const [state, setState] = useState(null);
  const [saving, setSaving] = useState(false);
  const [until, setUntil] = useState('');
  const [result, setResult] = useState(null);

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/admin/prelaunch/bookings`, auth());
      setState(r.data);
      setUntil(r.data.free_until ? String(r.data.free_until).slice(0, 10) : '');
    } catch {
      setState(null);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const apply = async (enabled) => {
    if (!enabled && state?.enabled) {
      const count = state.practitioner_count || 0;
      const ok = window.confirm(
        `Turn pre-launch off?\n\nBookings will start requiring a subscription immediately, and ` +
        `${count} practitioner${count === 1 ? '' : 's'} will be emailed and notified to set up ` +
        `direct deposit and start their subscription.\n\nThis is the launch-day switch.`
      );
      if (!ok) return;
    }
    setSaving(true); setResult(null);
    try {
      const r = await axios.put(
        `${API}/admin/prelaunch/bookings`,
        { enabled, free_until: until || null, notify: true },
        auth()
      );
      setState(s => ({ ...s, ...r.data }));
      if (r.data.practitioners_notified > 0) {
        setResult(`Launch notice sent to ${r.data.practitioners_notified} practitioner${r.data.practitioners_notified === 1 ? '' : 's'}.`);
      }
      load();
    } catch (e) {
      setResult(e.response?.data?.detail || "That didn't save.");
    } finally {
      setSaving(false);
    }
  };

  if (!state) return null;

  const on = state.enabled;

  return (
    <div
      className={`p-4 rounded-lg border ${on ? 'bg-amber-900/20 border-amber-700/40' : 'bg-slate-900/40 border-white/10'}`}
      data-testid="prelaunch-bookings-card"
    >
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="max-w-2xl">
          <p className={`text-sm font-semibold flex items-center gap-2 ${on ? 'text-amber-200' : 'text-gray-200'}`}>
            <Rocket className="w-4 h-4" />
            Bookings are free until launch
            <span
              className={`text-[10px] uppercase tracking-wide px-2 py-0.5 rounded ${on ? 'bg-amber-400/20 text-amber-200' : 'bg-white/10 text-gray-400'}`}
              data-testid="prelaunch-state-badge"
            >
              {on ? 'On' : 'Off'}
            </span>
          </p>
          <p className="text-xs text-gray-400 mt-1.5 leading-relaxed">
            While this is on, every practitioner can build availability, session types, client
            emails and their public booking page with no subscription — and no Stripe record is
            created, so nobody ends up with a sandbox subscription that dies at go-live.
            {' '}Turning it off is the launch-day switch: it restores the paywall and emails every
            practitioner to set up direct deposit and subscribe.
          </p>
          <p className="text-xs text-gray-500 mt-2 flex items-center gap-1.5" data-testid="prelaunch-practitioner-count">
            <Users className="w-3 h-3" />
            {state.practitioner_count} practitioner{state.practitioner_count === 1 ? '' : 's'} on the platform
            {state.launch_notice_sent_at && ' · launch notice already sent'}
          </p>

          <div className="flex items-center gap-2 mt-3">
            <label className="text-xs text-gray-400">Auto-expire on</label>
            <input
              type="date"
              value={until}
              onChange={e => setUntil(e.target.value)}
              className="bg-slate-800 border border-white/10 rounded px-2 py-1 text-xs text-gray-200"
              data-testid="prelaunch-until-input"
            />
            <span className="text-xs text-gray-500">optional — a date that passes ends it on its own</span>
          </div>
          {state.expired && (
            <p className="text-xs text-amber-300/80 mt-2" data-testid="prelaunch-expired-note">
              The date you set has passed, so the paywall is already back on. Clear the date or
              pick a new one to keep bookings free.
            </p>
          )}
          {result && (
            <p className="text-xs text-teal-300 mt-2" data-testid="prelaunch-result">{result}</p>
          )}
        </div>

        <div className="flex flex-col gap-2">
          <Button
            size="sm"
            onClick={() => apply(!on)}
            disabled={saving}
            className={on ? 'bg-slate-700 hover:bg-slate-600' : 'bg-amber-600 hover:bg-amber-500'}
            data-testid="prelaunch-toggle-btn"
          >
            {saving && <Loader2 className="w-3 h-3 mr-2 animate-spin" />}
            {on ? 'Turn off (we are live)' : 'Turn on'}
          </Button>
          {on && until && (
            <Button
              size="sm"
              variant="outline"
              onClick={() => apply(true)}
              disabled={saving}
              className="bg-gray-800 border-gray-600 text-gray-200 hover:bg-gray-700"
              data-testid="prelaunch-save-date-btn"
            >
              Save date
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
